/**
 * HOD Category Requests Component
 *
 * Displays maintenance requests specific to HOD's category
 * Allows review, approval, and worker assignment
 */

import React, { useState, useEffect, useCallback } from "react";
import Layout from "../common/Layout";
import { useAuth } from "../../context/AuthContext";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  IconButton,
  Tooltip,
  Badge,
  Tabs,
  Tab,
} from "@mui/material";
import { Visibility, Person, Refresh } from "@mui/icons-material";

const HODCategoryRequests = () => {
  const { currentUser } = useAuth();
  const [pendingRequests, setPendingRequests] = useState([]);
  const [assignedRequests, setAssignedRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [hodProfile, setHodProfile] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedWorker, setSelectedWorker] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [estimatedDuration, setEstimatedDuration] = useState("");
  const [hodNotes, setHodNotes] = useState("");
  const [assignmentDialog, setAssignmentDialog] = useState(false);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [reviewDecision, setReviewDecision] = useState("");
  const [hodComments, setHodComments] = useState("");
  const [estimatedPriority, setEstimatedPriority] = useState("");
  const [assignWorkerDuringReview, setAssignWorkerDuringReview] = useState("");
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadRequestsData = useCallback(async () => {
    try {
      setLoading(true);
      const token = await currentUser.getIdToken();

      // Get HOD profile
      const hodResponse = await fetch("/api/v1/hods/me", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!hodResponse.ok) {
        throw new Error("Failed to get HOD profile");
      }

      const currentHodProfile = await hodResponse.json();
      setHodProfile(currentHodProfile);

      // Get pending requests
      const pendingResponse = await fetch("/api/v1/hods/requests/pending", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const pendingData = await pendingResponse.json();

      if (!pendingResponse.ok) {
        throw new Error(
          pendingData.message || "Failed to load pending requests"
        );
      }

      setPendingRequests(pendingData);

      // Get assigned requests
      const assignedResponse = await fetch("/api/v1/hods/requests/assigned", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const assignedData = await assignedResponse.json();

      if (assignedResponse.ok) {
        setAssignedRequests(assignedData);
      }

      // Get all requests for the category
      const allResponse = await fetch("/api/v1/hods/requests/all", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const allData = await allResponse.json();

      if (allResponse.ok) {
        setAllRequests(allData);
      }

      // Get team workers
      const dashboardResponse = await fetch("/api/v1/hods/me/dashboard", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const dashboardData = await dashboardResponse.json();

      if (dashboardResponse.ok) {
        setWorkers(dashboardData.workers || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadRequestsData();
  }, [loadRequestsData]);

  const handleReviewRequest = async () => {
    try {
      setLoading(true);
      const token = await currentUser.getIdToken();

      const requestBody = {
        decision: reviewDecision,
        hodComments,
        estimatedPriority,
      };

      // Only include assignedWorkerId if a worker is actually selected
      if (assignWorkerDuringReview && assignWorkerDuringReview.trim() !== "") {
        requestBody.assignedWorkerId = assignWorkerDuringReview;
      }

      const response = await fetch(
        `/api/v1/hods/requests/${selectedRequest.id}/review`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to review request");
      }

      setSuccess(data.message || `Request ${reviewDecision} successfully`);
      setReviewDialog(false);
      loadRequestsData();
      resetDialogState();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignWorker = async () => {
    try {
      setLoading(true);
      const token = await currentUser.getIdToken();

      const response = await fetch(
        `/api/v1/hods/requests/${selectedRequest.id}/assign`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            workerId: selectedWorker,
            scheduledDate,
            estimatedDuration,
            hodNotes,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to assign worker");
      }

      setSuccess("Worker assigned successfully");
      setAssignmentDialog(false);
      loadRequestsData();
      resetDialogState();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetDialogState = () => {
    setSelectedRequest(null);
    setSelectedWorker("");
    setScheduledDate("");
    setEstimatedDuration("");
    setHodNotes("");
    setReviewDecision("");
    setHodComments("");
    setEstimatedPriority("");
    setAssignWorkerDuringReview("");
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "error";
      case "medium":
        return "warning";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending_hod_review":
        return "warning";
      case "assigned":
        return "info";
      case "in_progress":
        return "primary";
      case "completed":
        return "success";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const renderRequestsTable = (requests, showActions = true) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Token #</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Priority</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Requester</TableCell>
            <TableCell>Date</TableCell>
            {showActions && <TableCell>Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.map((request) => (
            <TableRow key={request.id}>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">
                  {request.tokenNumber}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {request.description?.substring(0, 60)}
                  {request.description?.length > 60 && "..."}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={request.priority || "Medium"}
                  color={getPriorityColor(request.priority)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={request.status?.replace(/_/g, " ")}
                  color={getStatusColor(request.status)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {request.location || "Not specified"}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {request.requesterName || request.requesterEmail}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption">
                  {new Date(request.createdAt).toLocaleDateString()}
                </Typography>
              </TableCell>
              {showActions && (
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          setSelectedRequest(request);
                          setReviewDialog(true);
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    {request.status === "pending_hod_review" && (
                      <Tooltip title="Assign Worker">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => {
                            setSelectedRequest(request);
                            setAssignmentDialog(true);
                          }}
                        >
                          <Person />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              )}
            </TableRow>
          ))}
          {requests.length === 0 && (
            <TableRow>
              <TableCell colSpan={showActions ? 8 : 7} align="center">
                <Typography variant="body2" color="text.secondary">
                  No requests found
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading && !pendingRequests.length) {
    return <Layout title="Category Requests">Loading requests...</Layout>;
  }

  return (
    <Layout title="Category Requests">
      <Box sx={{ width: "100%" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {/* Header Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="h5" gutterBottom>
                  {hodProfile?.category} Category Requests
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Review and manage maintenance requests for your category
                </Typography>
              </Box>
              <Button
                startIcon={<Refresh />}
                onClick={loadRequestsData}
                disabled={loading}
                variant="outlined"
              >
                Refresh
              </Button>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "warning.main",
                    color: "white",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h4">{pendingRequests.length}</Typography>
                  <Typography variant="body2">Pending Review</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "info.main",
                    color: "white",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h4">
                    {assignedRequests.length}
                  </Typography>
                  <Typography variant="body2">Assigned</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "success.main",
                    color: "white",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h4">
                    {allRequests.filter((r) => r.status === "completed").length}
                  </Typography>
                  <Typography variant="body2">Completed</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "primary.main",
                    color: "white",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h4">{allRequests.length}</Typography>
                  <Typography variant="body2">Total Requests</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Requests Tabs */}
        <Card>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
          >
            <Tab
              label={
                <Badge badgeContent={pendingRequests.length} color="error">
                  Pending Review
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={assignedRequests.length} color="info">
                  Assigned
                </Badge>
              }
            />
            <Tab label="All Requests" />
          </Tabs>

          <CardContent>
            {currentTab === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Requests Awaiting Your Review
                </Typography>
                {renderRequestsTable(pendingRequests)}
              </Box>
            )}

            {currentTab === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Assigned Requests
                </Typography>
                {renderRequestsTable(assignedRequests)}
              </Box>
            )}

            {currentTab === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  All Category Requests
                </Typography>
                {renderRequestsTable(allRequests, false)}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Review Dialog */}
        <Dialog
          open={reviewDialog}
          onClose={() => setReviewDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Review Request</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Streamlined Workflow:</strong> You can now approve
              requests and assign workers in one step. Worker assignment is
              optional - you can approve and assign later if needed.
            </Alert>
            {selectedRequest && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Request Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Token: {selectedRequest.tokenNumber}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {selectedRequest.description}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Location: {selectedRequest.location || "Not specified"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Requester:{" "}
                      {selectedRequest.requesterName ||
                        selectedRequest.requesterEmail}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Decision</InputLabel>
                      <Select
                        value={reviewDecision}
                        onChange={(e) => setReviewDecision(e.target.value)}
                      >
                        <MenuItem value="approved">Approve</MenuItem>
                        <MenuItem value="rejected">Reject</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Priority Assessment</InputLabel>
                      <Select
                        value={estimatedPriority}
                        onChange={(e) => setEstimatedPriority(e.target.value)}
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                      </Select>
                    </FormControl>
                    {reviewDecision === "approved" && (
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Assign Worker (Optional)</InputLabel>
                        <Select
                          value={assignWorkerDuringReview}
                          onChange={(e) =>
                            setAssignWorkerDuringReview(e.target.value)
                          }
                        >
                          <MenuItem value="">
                            <em>Assign Later</em>
                          </MenuItem>
                          {workers.map((worker) => (
                            <MenuItem key={worker.id} value={worker.id}>
                              {worker.name} -{" "}
                              {worker.specialization?.join(", ") || "General"}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="HOD Comments"
                      value={hodComments}
                      onChange={(e) => setHodComments(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReviewDialog(false)}>Cancel</Button>
            <Button
              onClick={handleReviewRequest}
              disabled={!reviewDecision || loading}
              variant="contained"
            >
              {loading
                ? "Processing..."
                : reviewDecision === "approved" && assignWorkerDuringReview
                ? "Approve & Assign Worker"
                : reviewDecision === "approved"
                ? "Approve Request"
                : "Submit Review"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Assignment Dialog */}
        <Dialog
          open={assignmentDialog}
          onClose={() => setAssignmentDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Assign Worker</DialogTitle>
          <DialogContent>
            {selectedRequest && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Request Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Token: {selectedRequest.tokenNumber}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {selectedRequest.description}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Select Worker</InputLabel>
                      <Select
                        value={selectedWorker}
                        onChange={(e) => setSelectedWorker(e.target.value)}
                      >
                        {workers.map((worker) => (
                          <MenuItem key={worker.id} value={worker.id}>
                            {worker.name} ({worker.currentTaskCount || 0} tasks)
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="Scheduled Date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      sx={{ mb: 2 }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      fullWidth
                      label="Estimated Duration (hours)"
                      type="number"
                      value={estimatedDuration}
                      onChange={(e) => setEstimatedDuration(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="HOD Notes"
                      value={hodNotes}
                      onChange={(e) => setHodNotes(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignmentDialog(false)}>Cancel</Button>
            <Button
              onClick={handleAssignWorker}
              disabled={!selectedWorker || loading}
              variant="contained"
            >
              Assign Worker
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default HODCategoryRequests;
